import { MemberCard } from '../shared/MemberCard';
import { MemberMiniCard } from '../shared/MemberMiniCard';
import { MiniCardExpand } from '../shared/MiniCardExpand';
import { miniFlexSx, editButtonOffsetSx } from '../../App';
import { EditableEquipmentItem, MemberEquipmentEditor } from '../shared/MemberEquipmentEditor';
import React from 'react';
import { alpha, Box, BottomNavigation, BottomNavigationAction, Button, Checkbox, Chip, Divider, Fab, FormControl, FormControlLabel, IconButton, InputLabel, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Select, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import AccessibilityIcon from '@mui/icons-material/Accessibility';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SettingsIcon from '@mui/icons-material/Settings';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { lightGreen, orange } from '@mui/material/colors';
import { useWarbandStore } from '../../stores/warbandStore';
import { StargraveIndexTab } from '../tabs/StargraveIndexTab';
import { StargraveCrewData, StargraveLeader, StargraveLeaderRole, StargraveSoldier } from '../../types';
import { stargraveBackgrounds, stargravePowers } from '../../data/stargrave/coreRules';
import { useStargraveEquipment, useStargraveSoldiers } from '../../stores/stargraveCustomStore';
import { stargraveCrewService } from '../../services/stargraveCrewService';
import { createSteelSurface, generateBloodSpatterSVG, hashStringToSeed } from '../../theme/surfaces';
import { PowerCard } from '../shared/PowerCard';
import { useSwipeTabs } from '../../hooks/useSwipeTabs';
import { SwipeTabs } from '../shared/SwipeTabs';
import { StargraveLeaderModal } from '../modals/StargraveLeaderModal';
import { StargraveRecruitSoldierModal } from '../modals/StargraveRecruitSoldierModal';
import { StargraveManageBuffsModal } from '../modals/StargraveManageBuffsModal';
import { ManageGoldModal } from '../modals/ManageGoldModal';
import { EditNameDialog } from '../shared/EditNameDialog';
import { formatStargraveBuffEffect, getStargraveBuffById, applyStargraveBuffsToStats, getStargraveBuffStatModifiers } from '../../game/stargraveBuffRules';
import { applyStargraveLootPenalties, getStargraveLootStatModifiers } from '../../game/stargravePlayRules';
import { ItemDetailsPopover } from '../shared/ItemDetailsPopover';
import { PopoverState } from '../../hooks/useItemPopover';
import { StargraveTechnologyModal } from '../modals/StargraveTechnologyModal';
import { stargraveAdvancedTechnology } from '../../data/stargrave/advancedTechnology';
import { StargraveShipUpgradeModal } from '../modals/StargraveShipUpgradeModal';
import { stargraveShipUpgrades } from '../../data/stargrave/shipUpgrades';
import { ConfirmActionDialog } from '../shared/ConfirmActionDialog';
import { StargravePlayTab } from '../tabs/StargravePlayTab';
import { StargravePlayPowersTab } from '../tabs/StargravePlayPowersTab';
import { StargravePostGameTab } from '../tabs/StargravePostGameTab';
import { StargravePreGameTab } from '../tabs/StargravePreGameTab';
import { canAddPermanentInjury, getEffectiveLeaderStats } from '../../game/stargraveCampaignRules';
import { applyStargraveGearModifiers, getStargraveGearStatModifiers } from '../../game/stargraveLeaderRules';
import { applyStargraveTechnologyModifiers, getStargraveTechnologyEffects, getStargraveTechnologyStatModifiers } from '../../game/stargraveTechRules';
import { UseTechnologyDialog } from '../shared/UseTechnologyDialog';
import { stargravePermanentInjuries } from '../../data/stargrave/permanentInjuries';
import { StargraveAdvanceLeaderModal } from '../modals/StargraveAdvanceLeaderModal';
import { StargraveAdvancedWeaponModal } from '../modals/StargraveAdvancedWeaponModal';
import { stargraveAdvancedWeapons } from '../../data/stargrave/advancedWeapons';
import { StargraveAlienArtefactModal } from '../modals/StargraveAlienArtefactModal';
import { stargraveAlienArtefacts } from '../../data/stargrave/alienArtefacts';
import { StargraveSecretModal } from '../modals/StargraveSecretModal';
import { SelectPermanentInjuryModal } from '../modals/SelectPermanentInjuryModal';
import { useAuthStore } from '../../stores/authStore';
import { SharedMemberStatsAndHealth, buildStargraveSharedStats, buildStargraveMiniStats } from '../shared/SharedMemberStatsAndHealth';
import { getStargraveSoldierTraits, formatStargraveSoldierCategory, applyStargraveRewireModifiers, getStargraveRewireStatModifiers } from '../../game/stargraveSoldierRules';

interface RosterActionTarget {
  id: string;
  name: string;
  roleLabel: string;
}

interface InventoryAction {
  kind: 'technology' | 'weapon' | 'artefact' | 'shipUpgrade';
  instanceId: string;
  itemName: string;
  action: 'sell' | 'discard';
  salePrice: number;
}

const panelSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  p: { xs: 2, sm: 3 },
} as const;

// Steel deck-plate panel for tab content
const steelPanelSx = {
  ...panelSx,
  ...createSteelSurface(33),
  borderRadius: 1,
  m: { xs: 1, sm: 2 },
} as const;

export const StargraveCrewPage: React.FC = () => {
  const stargraveEquipment = useStargraveEquipment();
  const stargraveSoldiers = useStargraveSoldiers();
  const [advancedInventoryTab, setAdvancedInventoryTab] = React.useState(0);
  const [leaderRole, setLeaderRole] = React.useState<StargraveLeaderRole | null>(null);
  const [recruitOpen, setRecruitOpen] = React.useState(false);
  const [recruitTemporaryOpen, setRecruitTemporaryOpen] = React.useState(false);
  const [powerPopover, setPowerPopover] = React.useState<PopoverState | null>(null);
  const [technologyPopoverInstanceId, setTechnologyPopoverInstanceId] = React.useState<string | null>(null);
  const [technologyModalOpen, setTechnologyModalOpen] = React.useState(false);
  const [shipUpgradeModalOpen, setShipUpgradeModalOpen] = React.useState(false);
  const [shipName, setShipName] = React.useState('');
  const [editingShipName, setEditingShipName] = React.useState(false);
  const [rosterMenuAnchor, setRosterMenuAnchor] = React.useState<HTMLElement | null>(null);
  const [rosterTarget, setRosterTarget] = React.useState<RosterActionTarget | null>(null);
  const [editingLeader, setEditingLeader] = React.useState<StargraveLeader | null>(null);
  const [editingLeaderPowersOnly, setEditingLeaderPowersOnly] = React.useState(false);
  const [editingSoldier, setEditingSoldier] = React.useState<StargraveSoldier | null>(null);
  const [removeTarget, setRemoveTarget] = React.useState<RosterActionTarget | null>(null);
  const [advanceModalOpen, setAdvanceModalOpen] = React.useState(false);
  const [advancedWeaponModalOpen, setAdvancedWeaponModalOpen] = React.useState(false);
  const [alienArtefactModalOpen, setAlienArtefactModalOpen] = React.useState(false);
  const [secretModalOpen, setSecretModalOpen] = React.useState(false);
  const [secretAction, setSecretAction] = React.useState<'sell' | 'discard' | null>(null);
  const [inventoryAction, setInventoryAction] = React.useState<InventoryAction | null>(null);
  const [refundOnDiscard, setRefundOnDiscard] = React.useState(false);
  const [injuryLeader, setInjuryLeader] = React.useState<StargraveLeader | null>(null);
  const [manageBuffsMemberId, setManageBuffsMemberId] = React.useState<string | null>(null);
  const [buffDelete, setBuffDelete] = React.useState<{ memberId: string; buffId: string } | null>(null);
  const [useTechnologyTargetId, setUseTechnologyTargetId] = React.useState<string | null>(null);
  const [editCrewStat, setEditCrewStat] = React.useState<'gold' | 'experience' | null>(null);
  const [renameTarget, setRenameTarget] = React.useState<{ id: string; name: string; kind: 'leader' | 'soldier' } | null>(null);
  const selectedWarbandId = useWarbandStore((state) => state.selectedWarbandId);
  const setWarbands = useWarbandStore((state) => state.setWarbands);
  const currentPage = useWarbandStore((state) => state.currentPage);
  const lastNonManagePage = useWarbandStore((state) => state.lastNonManagePage);
  const manageTabIndex = useWarbandStore((state) => state.manageTabIndex);
  const setManageTabIndex = useWarbandStore((state) => state.setManageTabIndex);
  const playTabIndex = useWarbandStore((state) => state.playTabIndex);
  const setPlayTabIndex = useWarbandStore((state) => state.setPlayTabIndex);
  const navigateToPage = useWarbandStore((state) => state.navigateToPage);
  const isMinified = useWarbandStore((state) => state.isMinified);
  const setIsMinified = useWarbandStore((state) => state.setIsMinified);
  const accessToken = useAuthStore((state) => state.accessToken);
  const crew = useWarbandStore((state) =>
    state.warbands.find((warband) => warband.id === state.selectedWarbandId)
  );
  const [crewData, setCrewData] = React.useState<StargraveCrewData>({
    captain: null,
    firstMate: null,
    soldiers: [],
    advancedTechnology: [],
    advancedWeapons: [],
    alienArtefacts: [],
    secrets: 0,
    ship: { name: 'Unnamed Ship', upgradeIds: [] },
  });

  React.useEffect(() => {
    if (!selectedWarbandId) return;
    setCrewData(stargraveCrewService.get(selectedWarbandId));
    setShipName(stargraveCrewService.get(selectedWarbandId).ship.name);
    const persistedWarband = stargraveCrewService.getWarband(selectedWarbandId);
    if (persistedWarband) {
      setWarbands(useWarbandStore.getState().warbands.map((warband) =>
        warband.id === selectedWarbandId ? persistedWarband : warband
      ));
    }
  }, [selectedWarbandId, setWarbands]);

  const isPlayMode = currentPage === 'play';
  const isPreGameMode = currentPage === 'pregame';
  const isPostGameMode = currentPage === 'postgame';
  const isManageMode = currentPage === 'manage';
  const tabsSurface = createSteelSurface(21);
  const { containerRef: playContainerRef, trackRef: playTrackRef } = useSwipeTabs(
    playTabIndex,
    3,
    setPlayTabIndex,
    isPlayMode
  );
  const { containerRef: manageContainerRef, trackRef: manageTrackRef } = useSwipeTabs(
    manageTabIndex,
    5,
    setManageTabIndex,
    isManageMode
  );
  if (!selectedWarbandId || !crew) return null;
  const saveLeader = (leader: StargraveLeader) => {
    setCrewData(stargraveCrewService.saveLeader(selectedWarbandId, leader));
  };

  const saveEditedLeader = (leader: StargraveLeader) => {
    setCrewData(stargraveCrewService.updateLeader(selectedWarbandId, leader));
  };

  const recruitSoldier = (input: { profileId: string; name?: string; notes?: string; isRobot: boolean }) => {
    const result = stargraveCrewService.recruitSoldier(selectedWarbandId, input);
    setCrewData(result.stargrave);
    setWarbands(useWarbandStore.getState().warbands.map((warband) =>
      warband.id === selectedWarbandId ? result.warband : warband
    ));
  };

  const editSoldier = (input: { profileId: string; name?: string; notes?: string; isRobot: boolean }) => {
    if (!editingSoldier) return;
    const result = stargraveCrewService.updateSoldier(selectedWarbandId, editingSoldier.id, input);
    setCrewData(result.stargrave);
    setWarbands(useWarbandStore.getState().warbands.map((warband) =>
      warband.id === selectedWarbandId ? result.warband : warband
    ));
  };

  const updateManagedHealth = (memberId: string, nextHealth: number) => {
    setCrewData(stargraveCrewService.updateMemberGameState(selectedWarbandId, memberId, {
      currentHealth: nextHealth,
    }));
  };

  const findCrewMember = (memberId: string | null) => {
    if (!memberId) return undefined;
    if (crewData.captain?.id === memberId) return crewData.captain;
    if (crewData.firstMate?.id === memberId) return crewData.firstMate;
    return crewData.soldiers.find((soldier) => soldier.id === memberId);
  };

  const buildManagedEffects = (memberId: string, activeBuffs: StargraveCrewData['soldiers'][number]['gameState']['activeBuffs']) =>
    activeBuffs.flatMap((active) => {
      const buff = getStargraveBuffById(active.buffId);
      return buff ? [{
        id: buff.id,
        name: active.stackCount > 1 ? `${buff.name} ×${active.stackCount}` : buff.name,
        description: buff.summary,
        modifiers: formatStargraveBuffEffect(buff),
        onDelete: () => setBuffDelete({ memberId, buffId: buff.id }),
      }] : [];
    });

  const openPowerPopover = (
    event: React.MouseEvent<HTMLElement>,
    selection: StargraveLeader['powers'][number]
  ) => {
    const power = stargravePowers.find((candidate) => candidate.id === selection.powerId);
    if (!power) return;
    setTechnologyPopoverInstanceId(null);
    setPowerPopover({
      anchor: event.currentTarget,
      id: power.id,
      itemType: 'spell',
      name: power.name,
      description: power.summary,
      tags: [],
      modifiers: `Activation ${selection.activation} | Strain ${selection.strain} | ${power.timing}`,
    });
  };

  const openEquipmentPopover = (event: React.MouseEvent<HTMLElement>, equipmentId: string) => {
    const equipment = stargraveEquipment.find((candidate) => candidate.id === equipmentId);
    if (!equipment) return;
    const modifiers = [
      `${equipment.gearSlots} gear slot${equipment.gearSlots === 1 ? '' : 's'}`,
      equipment.damageModifier !== undefined ? `Damage ${equipment.damageModifier >= 0 ? '+' : ''}${equipment.damageModifier}` : null,
      equipment.maxRange !== undefined ? `Range ${equipment.maxRange}"` : null,
      equipment.armourModifier !== undefined ? `Armour ${equipment.armourModifier >= 0 ? '+' : ''}${equipment.armourModifier}` : null,
      equipment.moveModifier !== undefined ? `Move ${equipment.moveModifier}` : null,
    ].filter(Boolean).join(' | ');
    setTechnologyPopoverInstanceId(null);
    setPowerPopover({
      anchor: event.currentTarget,
      id: equipment.id,
      itemType: equipment.category === 'weapon' ? 'weapon' : undefined,
      name: equipment.name,
      description: equipment.notes ?? null,
      tags: [],
      modifiers,
    });
  };

  const applyTechnologyResult = (result: { stargrave: StargraveCrewData; warband: typeof crew }) => {
    setCrewData(result.stargrave);
    setWarbands(useWarbandStore.getState().warbands.map((warband) =>
      warband.id === selectedWarbandId ? result.warband : warband
    ));
  };

  const saveRename = (name: string) => {
    if (!renameTarget) return;
    if (renameTarget.kind === 'leader') {
      const leader = [crewData.captain, crewData.firstMate].find((candidate) => candidate?.id === renameTarget.id);
      if (leader) setCrewData(stargraveCrewService.updateLeader(selectedWarbandId, { ...leader, name }));
    } else {
      const soldier = crewData.soldiers.find((candidate) => candidate.id === renameTarget.id);
      if (soldier) {
        const result = stargraveCrewService.updateSoldier(selectedWarbandId, soldier.id, {
          profileId: soldier.profileId, name, notes: soldier.notes, isRobot: soldier.isRobot,
        });
        setCrewData(result.stargrave);
        setWarbands(useWarbandStore.getState().warbands.map((warband) =>
          warband.id === selectedWarbandId ? result.warband : warband
        ));
      }
    }
    setRenameTarget(null);
  };

  const technologyOwners = [
    ...(crewData.captain ? [{ id: crewData.captain.id, name: crewData.captain.name }] : []),
    ...(crewData.firstMate ? [{ id: crewData.firstMate.id, name: crewData.firstMate.name }] : []),
    ...crewData.soldiers.map((soldier) => ({ id: soldier.id, name: soldier.name })),
  ];
  const technologyPopoverInstance = technologyPopoverInstanceId
    ? crewData.advancedTechnology.find((item) => item.instanceId === technologyPopoverInstanceId)
    : undefined;

  const renderCombinedEquipment = (ownerId: string, equipmentIds: string[]) => {
    const ownerName = technologyOwners.find((owner) => owner.id === ownerId)?.name ?? 'Crew Member';
    const ownerLeader = [crewData.captain, crewData.firstMate].find((leader) => leader?.id === ownerId) ?? null;
    const buildEquipmentItem = (id: string) => {
      const equipment = stargraveEquipment.find((item) => item.id === id);
      return equipment ? {
        id: `equipment-${id}`,
        name: equipment.name,
        category: equipment.category === 'weapon' ? 'Weapon' : equipment.category === 'armour' ? 'Armour' : 'Special',
        onClick: (event: React.MouseEvent<HTMLElement>) => openEquipmentPopover(event, id),
        onRemove: ownerLeader ? () => setCrewData(stargraveCrewService.updateLeader(selectedWarbandId, {
          ...ownerLeader,
          gearIds: ownerLeader.gearIds.filter((gearId) => gearId !== id),
        })) : undefined,
      } : null;
    };
    const mundaneItems = (ownerLeader ? stargraveEquipment.map((equipment) => equipment.id) : equipmentIds)
      .map(buildEquipmentItem)
      .filter((item): item is NonNullable<typeof item> => Boolean(item));
    const advancedItems: EditableEquipmentItem[] = [
      ...crewData.advancedTechnology.map((instance) => {
        const technology = stargraveAdvancedTechnology.find((item) => item.id === instance.technologyId);
        const ownerName = instance.ownerId ? technologyOwners.find((owner) => owner.id === instance.ownerId)?.name : undefined;
        return technology ? { id: instance.instanceId, name: technology.name, category: 'Special', description: technology.summary, disabled: Boolean(instance.ownerId && instance.ownerId !== ownerId), disabledReason: ownerName ? `Assigned to ${ownerName}` : undefined } : null;
      }),
      ...crewData.advancedWeapons.filter((instance) => {
        const weapon = stargraveAdvancedWeapons.find((item) => item.id === instance.weaponId);
        return Boolean(weapon && equipmentIds.includes(weapon.baseWeaponId));
      }).map((instance) => {
        const weapon = stargraveAdvancedWeapons.find((item) => item.id === instance.weaponId);
        const ownerName = instance.ownerId ? technologyOwners.find((owner) => owner.id === instance.ownerId)?.name : undefined;
        return weapon ? { id: instance.instanceId, name: weapon.name, category: 'Weapon', description: weapon.summary, disabled: Boolean(instance.ownerId && instance.ownerId !== ownerId), disabledReason: ownerName ? `Assigned to ${ownerName}` : undefined } : null;
      }),
      ...crewData.alienArtefacts.filter((instance) => {
        return Boolean(crewData.captain?.id === ownerId || crewData.firstMate?.id === ownerId);
      }).map((instance) => {
        const artefact = stargraveAlienArtefacts.find((item) => item.id === instance.artefactId);
        const ownerName = instance.ownerId ? technologyOwners.find((owner) => owner.id === instance.ownerId)?.name : undefined;
        return artefact ? { id: instance.instanceId, name: artefact.name, category: 'Special', description: artefact.summary, disabled: Boolean(instance.ownerId && instance.ownerId !== ownerId), disabledReason: ownerName ? `Assigned to ${ownerName}` : undefined } : null;
      }),
    ].filter((item): item is NonNullable<typeof item> => Boolean(item));
    const modalItems = [...mundaneItems, ...advancedItems];
    const items: EditableEquipmentItem[] = [
      ...getVisibleEquipmentIds(ownerId, equipmentIds).map(buildEquipmentItem),
      ...crewData.advancedTechnology.filter((instance) => instance.ownerId === ownerId).map((instance) => {
        const technology = stargraveAdvancedTechnology.find((item) => item.id === instance.technologyId);
        return technology ? { id: instance.instanceId, name: technology.name, onClick: (event: React.MouseEvent<HTMLElement>) => {
          setTechnologyPopoverInstanceId(instance.instanceId);
          setPowerPopover({ anchor: event.currentTarget, id: technology.id, name: technology.name, description: technology.summary, tags: [], modifiers: `${technology.gearSlots} slot${technology.gearSlots === 1 ? '' : 's'}` });
        }, onRemove: () => setCrewData(stargraveCrewService.assignTechnology(selectedWarbandId, instance.instanceId, null)) } : null;
      }),
      ...crewData.advancedWeapons.filter((instance) => instance.ownerId === ownerId).map((instance) => {
        const weapon = stargraveAdvancedWeapons.find((item) => item.id === instance.weaponId);
        return weapon ? { id: instance.instanceId, name: weapon.name, onClick: (event: React.MouseEvent<HTMLElement>) => setPowerPopover({ anchor: event.currentTarget, id: weapon.id, itemType: 'weapon', name: weapon.name, description: weapon.summary, tags: [], modifiers: 'Indestructible' }), onRemove: () => setCrewData(stargraveCrewService.assignAdvancedWeapon(selectedWarbandId, instance.instanceId, null)) } : null;
      }),
      ...crewData.alienArtefacts.filter((instance) => instance.ownerId === ownerId).map((instance) => {
        const artefact = stargraveAlienArtefacts.find((item) => item.id === instance.artefactId);
        return artefact ? { id: instance.instanceId, name: artefact.name, onClick: (event: React.MouseEvent<HTMLElement>) => setPowerPopover({ anchor: event.currentTarget, id: artefact.id, name: artefact.name, description: artefact.summary, tags: [], modifiers: 'Captain or First Mate only' }), onRemove: () => setCrewData(stargraveCrewService.assignAlienArtefact(selectedWarbandId, instance.instanceId, null)) } : null;
      }),
    ].filter((item): item is NonNullable<typeof item> => Boolean(item));
    const selectedModalItems = modalItems.filter((item) => ownerLeader
      ? ownerLeader.gearIds.includes(item.id.replace('equipment-', '')) || items.some((current) => current.id === item.id)
      : items.some((current) => current.id === item.id));
    return <MemberEquipmentEditor title="Equipment" editMode items={items}
      modalMemberName={ownerName}
      modalAvailableItems={modalItems}
      modalSelectedItems={selectedModalItems}
      onModalChange={(selectedIds) => {
        let nextCrew = crewData;
        if (ownerLeader) {
          const selectedGearIds = selectedIds
            .filter((id) => id.startsWith('equipment-'))
            .map((id) => id.replace('equipment-', ''));
          nextCrew = stargraveCrewService.updateLeader(selectedWarbandId, {
            ...ownerLeader,
            gearIds: selectedGearIds,
          });
        }
        for (const instance of crewData.advancedTechnology) {
          const shouldOwn = selectedIds.includes(instance.instanceId);
          if (shouldOwn !== (instance.ownerId === ownerId)) nextCrew = stargraveCrewService.assignTechnology(selectedWarbandId, instance.instanceId, shouldOwn ? ownerId : null);
        }
        for (const instance of crewData.advancedWeapons) {
          const shouldOwn = selectedIds.includes(instance.instanceId);
          if (shouldOwn !== (instance.ownerId === ownerId)) nextCrew = stargraveCrewService.assignAdvancedWeapon(selectedWarbandId, instance.instanceId, shouldOwn ? ownerId : null);
        }
        for (const instance of crewData.alienArtefacts) {
          const shouldOwn = selectedIds.includes(instance.instanceId);
          if (shouldOwn !== (instance.ownerId === ownerId)) nextCrew = stargraveCrewService.assignAlienArtefact(selectedWarbandId, instance.instanceId, shouldOwn ? ownerId : null);
        }
        setCrewData(nextCrew);
      }} />;
  };

  const getVisibleEquipmentIds = (ownerId: string, equipmentIds: string[]) => {
    const replacedIds = new Set(crewData.advancedWeapons
      .filter((instance) => instance.ownerId === ownerId)
      .map((instance) => stargraveAdvancedWeapons.find((weapon) => weapon.id === instance.weaponId)?.baseWeaponId)
      .filter((id): id is string => Boolean(id)));
    return equipmentIds.filter((id) => !replacedIds.has(id));
  };

  const advancedInventoryTabs = (
    <Tabs value={advancedInventoryTab} onChange={(_, value) => setAdvancedInventoryTab(value)} variant="fullWidth">
      <Tab label="Technology" />
      <Tab label="Weapons" />
      <Tab label="Artefacts" />
    </Tabs>
  );

  const openRosterMenu = (event: React.MouseEvent<HTMLElement>, target: RosterActionTarget) => {
    setRosterMenuAnchor(event.currentTarget);
    setRosterTarget(target);
  };

  const closeRosterMenu = () => {
    setRosterMenuAnchor(null);
    setRosterTarget(null);
  };

  const bloodOverlay = (memberId: string, currentHealth: number) => currentHealth <= 0
    ? <Box aria-hidden style={{ position: 'absolute', inset: 0, zIndex: 2 }}
        sx={{ borderRadius: 'inherit', pointerEvents: 'none', backgroundImage: generateBloodSpatterSVG(hashStringToSeed(memberId)), backgroundRepeat: 'no-repeat', backgroundSize: 'cover', backgroundPosition: 'center' }} />
    : undefined;

  const renderLeader = (role: StargraveLeaderRole, leader: StargraveLeader | null) => {
    const roleLabel = role === 'captain' ? 'Captain' : 'First Mate';
    if (!leader) {
      return <Box>
        <Typography variant="h6">{roleLabel}</Typography>
        <Button variant="contained" onClick={() => setLeaderRole(role)}>Create {roleLabel}</Button>
      </Box>;
    }
    const background = stargraveBackgrounds.find((candidate) => candidate.id === leader.backgroundId);
    const equippedStats = applyStargraveTechnologyModifiers(
      applyStargraveGearModifiers(getEffectiveLeaderStats(leader), leader.gearIds),
      crewData.advancedTechnology,
      leader.id
    );
    const buffedStats = applyStargraveBuffsToStats(equippedStats, leader.gameState.activeBuffs);
    const effectiveStats = applyStargraveLootPenalties(buffedStats, leader.gameState.loot);
    const leaderHealth = { current: leader.gameState.currentHealth, max: equippedStats.health, onChange: (next: number) => updateManagedHealth(leader.id, next) };
    const sharedStats = buildStargraveSharedStats(leader.stats, effectiveStats, (stat) => [
      ...leader.permanentInjuryIds.flatMap((injuryId) => {
        const injury = stargravePermanentInjuries.find((candidate) => candidate.id === injuryId);
        const modifier = injury?.statModifiers?.[stat];
        return !injury || modifier === undefined ? [] : [{ label: injury.name, value: modifier, type: 'add' as const }];
      }),
      ...getStargraveGearStatModifiers(leader.gearIds, stat),
      ...getStargraveTechnologyStatModifiers(crewData.advancedTechnology, leader.id, stat),
      ...getStargraveBuffStatModifiers(leader.gameState.activeBuffs, stat),
      ...getStargraveLootStatModifiers(buffedStats, leader.gameState.loot, stat),
    ]);
    const leaderCard = <MemberCard
      warbandId={selectedWarbandId}
      memberId={leader.id}
      name={leader.name}
      subtitle={`${roleLabel} | ${background?.name} | Level ${leader.level}`}
      accessToken={accessToken}
      editMode
      onEdit={() => setRenameTarget({ id: leader.id, name: leader.name, kind: 'leader' })}
      onActions={(event) => openRosterMenu(event, { id: leader.id, name: leader.name, roleLabel })}
      statsContent={<SharedMemberStatsAndHealth stats={sharedStats} />}
      health={leaderHealth}
      overlay={bloodOverlay(leader.id, leader.gameState.currentHealth)}
      injuries={leader.permanentInjuryIds.flatMap((id) => {
        const injury = stargravePermanentInjuries.find((candidate) => candidate.id === id);
        return injury ? [{ id: injury.id, name: injury.name, description: injury.summary }] : [];
      })}
      onAddInjury={() => setInjuryLeader(leader)}
      onRemoveInjury={(_, index) => setCrewData(stargraveCrewService.removeLeaderPermanentInjury(selectedWarbandId, leader.id, index))}
      effects={[...buildManagedEffects(leader.id, leader.gameState.activeBuffs), ...getStargraveTechnologyEffects(crewData.advancedTechnology, leader.id)]}
      onAddEffect={() => setManageBuffsMemberId(leader.id)}
      lowerContent={<>
      {renderCombinedEquipment(leader.id, leader.gearIds)}
      <Box sx={{ mt: 2 }}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 700 }}>Powers</Typography>
          <IconButton size="small" sx={editButtonOffsetSx} aria-label={`Edit ${leader.name} powers`}
            onClick={() => { setEditingLeaderPowersOnly(true); setEditingLeader(leader); }}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {leader.powers.map((selection) => {
            const power = stargravePowers.find((candidate) => candidate.id === selection.powerId);
            return power ? (
              <Chip key={selection.powerId} size="small" clickable
                label={`${power.name} ${selection.activation} (${selection.strain})`}
                onClick={(event) => openPowerPopover(event, selection)} />
            ) : null;
          })}
        </Box>
      </Box>
      </>}
      notes={leader.notes}
      onSaveNotes={(notes) => setCrewData(stargraveCrewService.updateLeader(selectedWarbandId, { ...leader, notes }))}
    />;
    if (!isMinified) return leaderCard;
    return <MemberMiniCard
      warbandId={selectedWarbandId}
      memberId={leader.id}
      name={leader.name}
      subtitle={`${roleLabel} | Level ${leader.level}`}
      accessToken={accessToken}
      editMode
      health={leaderHealth}
      stats={buildStargraveMiniStats(effectiveStats)}
      overlay={bloodOverlay(leader.id, leader.gameState.currentHealth)}
      fullCard={leaderCard}
    />;
  };

  return (
    <Box sx={{
      display: 'flex',
      flex: 1,
      minHeight: 0,
      flexDirection: 'column',
      overflowY: 'auto',
      boxSizing: 'border-box',
      // SwipeTabs panelPb provides bottom-nav clearance in manage/play modes.
      pb: 0,
    }}>

      {isManageMode && <SwipeTabs containerRef={manageContainerRef} trackRef={manageTrackRef} numTabs={5} panelPb={10}>
        <Box key="crew" sx={steelPanelSx}>
          <Typography variant="h5">{crew.name}</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start', gap: 1 }}>
            <Chip label={`${crew.gold}cr`} clickable onClick={() => setEditCrewStat('gold')} />
            <Chip label={`${crew.experience} XP`} clickable onClick={() => setEditCrewStat('experience')} />
            <Button size="small" variant="outlined" disabled={crew.experience < 100 || (!crewData.captain && !crewData.firstMate)}
              onClick={() => setAdvanceModalOpen(true)}>Spend XP</Button>
          </Box>
          {crew.description && <Typography color="text.secondary">{crew.description}</Typography>}
          <Divider />
          <Box sx={isMinified ? miniFlexSx : { display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2, alignItems: 'stretch' }}>
            {renderLeader('captain', crewData.captain)}
            {renderLeader('firstMate', crewData.firstMate)}
          </Box>
          <Divider />
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
            <Typography variant="h6">Crew ({crewData.soldiers.length})</Typography>
          </Stack>
          {crewData.soldiers.length === 0 ? <Typography color="text.secondary">No crew members</Typography> : (
            <Box sx={isMinified ? miniFlexSx : { display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
              {crewData.soldiers.map((soldier) => {
                const profile = stargraveSoldiers.find((candidate) => candidate.id === soldier.profileId);
                if (!profile) return null;
                const soldierBuffed = applyStargraveBuffsToStats(
                  applyStargraveTechnologyModifiers(applyStargraveRewireModifiers(profile, soldier.rewiredStat), crewData.advancedTechnology, soldier.id),
                  soldier.gameState.activeBuffs
                );
                const soldierEffective = applyStargraveLootPenalties(soldierBuffed, soldier.gameState.loot);
                const soldierHealth = { current: soldier.gameState.currentHealth, max: profile.health, onChange: (next: number) => updateManagedHealth(soldier.id, next) };
                const soldierCard = <MemberCard
                  key={soldier.id}
                  warbandId={selectedWarbandId}
                  memberId={soldier.id}
                  name={soldier.name}
                  subtitle={`${profile.name} | ${formatStargraveSoldierCategory(profile.category)}`}
                  accessToken={accessToken}
                  editMode
                  statusContent={<>
                    {soldier.storedInShip && <Chip size="small" label="Stored In Ship" />}
                    {soldier.isTemporary && <Chip size="small" label="Temporary" />}
                  </>}
                  opacity={soldier.storedInShip ? 0.7 : 1}
                  overlay={bloodOverlay(soldier.id, soldier.gameState.currentHealth)}
                  onEdit={() => setRenameTarget({ id: soldier.id, name: soldier.name, kind: 'soldier' })}
                  onActions={(event) => openRosterMenu(event, { id: soldier.id, name: soldier.name, roleLabel: 'Soldier' })}
                  statsContent={<SharedMemberStatsAndHealth stats={buildStargraveSharedStats(
                    profile,
                    soldierEffective,
                    (stat) => [
                      ...getStargraveRewireStatModifiers(soldier.rewiredStat, stat),
                      ...getStargraveTechnologyStatModifiers(crewData.advancedTechnology, soldier.id, stat),
                      ...getStargraveBuffStatModifiers(soldier.gameState.activeBuffs, stat),
                      ...getStargraveLootStatModifiers(soldierBuffed, soldier.gameState.loot, stat),
                    ]
                  )} />}
                  health={soldierHealth}
                  lowerContent={<>
                    {renderCombinedEquipment(soldier.id, profile.equipmentIds)}
                  </>}
                  traits={getStargraveSoldierTraits(soldier.isRobot, profile, soldier.rewiredStat)}
                  effects={[...buildManagedEffects(soldier.id, soldier.gameState.activeBuffs), ...getStargraveTechnologyEffects(crewData.advancedTechnology, soldier.id)]}
                  onAddEffect={() => setManageBuffsMemberId(soldier.id)}
                  notes={soldier.notes}
                  onSaveNotes={(notes) => editSoldier({ profileId: soldier.profileId, name: soldier.name, notes, isRobot: soldier.isRobot })}
                />;
                if (!isMinified) return soldierCard;
                return <MemberMiniCard
                  key={soldier.id}
                  warbandId={selectedWarbandId}
                  memberId={soldier.id}
                  name={soldier.name}
                  subtitle={`${profile.name} | ${formatStargraveSoldierCategory(profile.category)}`}
                  accessToken={accessToken}
                  editMode
                  health={soldierHealth}
                  stats={buildStargraveMiniStats(profile)}
                  statusContent={soldier.storedInShip ? <Chip size="small" label="Stored" /> : undefined}
                  opacity={soldier.storedInShip ? 0.7 : 1}
                  overlay={bloodOverlay(soldier.id, soldier.gameState.currentHealth)}
                  fullCard={soldierCard}
                />;
              })}
            </Box>
          )}
        </Box>

        <Box key="powers" sx={steelPanelSx}>
          <Typography variant="h5">Powers</Typography>
          {([crewData.captain, crewData.firstMate] as Array<StargraveLeader | null>).map((leader) => leader && (
            <Box key={leader.id}>
              <Typography variant="h6" sx={{ mb: 1 }}>{leader.name}</Typography>
              <Box sx={isMinified ? miniFlexSx : { display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                {leader.powers.map((selection) => {
                  const selectedPower = stargravePowers.find((power) => power.id === selection.powerId);
                  return selectedPower ? <PowerCard
                    key={selection.powerId}
                    name={selectedPower.name}
                    activation={selection.activation}
                    strain={selection.strain}
                    timing={selectedPower.timing}
                    summary={selectedPower.summary}
                    minified={isMinified}
                  /> : null;
                })}
              </Box>
            </Box>
          ))}
          {!crewData.captain && !crewData.firstMate && <Typography color="text.secondary">Create a leader to select powers.</Typography>}
        </Box>

        <Box key="cargo" sx={steelPanelSx}>
          {advancedInventoryTabs}
          {advancedInventoryTab === 0 && <>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
            <Typography variant="h5">Advanced Technology</Typography>
          </Stack>
          {crewData.advancedTechnology.length === 0 ? (
            <Typography color="text.secondary">No advanced technology stored.</Typography>
          ) : (
            <Box sx={isMinified ? miniFlexSx : { display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
              {crewData.advancedTechnology.map((instance) => {
                const technology = stargraveAdvancedTechnology.find((item) => item.id === instance.technologyId);
                if (!technology) return null;
                const technologyCard = (mini: boolean): React.ReactElement => <Paper variant="outlined" sx={{ p: 2, ...(mini ? { width: 280, minHeight: 140, boxSizing: 'border-box' } : {}) }}>
                  <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                    <Typography variant="h6" noWrap={mini} sx={{ flex: 1, minWidth: 0 }}>{technology.name}</Typography>
                    {mini && <MiniCardExpand label={technology.name} fullCard={technologyCard(false)} />}
                  </Stack>
                  {!mini && <Typography variant="body2" sx={{ my: 1 }}>{technology.summary}</Typography>}
                  {!mini && technology.restriction && <Typography variant="body2" color="text.secondary">{technology.restriction}</Typography>}
                  <Stack direction="row" spacing={1} sx={{ my: 1, flexWrap: 'wrap' }}>
                    <Chip size="small" label={`${technology.gearSlots} slot${technology.gearSlots === 1 ? '' : 's'}`} />
                    {instance.remainingUses !== undefined && <Chip size="small" label={`${instance.remainingUses} uses`} />}
                    <Chip size="small" label={`Sell ${technology.salePrice}cr`} />
                  </Stack>
                  <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                    <InputLabel id={`technology-owner-${instance.instanceId}`}>Owner</InputLabel>
                    <Select labelId={`technology-owner-${instance.instanceId}`} label="Owner" value={instance.ownerId ?? ''}
                      displayEmpty
                      renderValue={(value) => technologyOwners.find((owner) => owner.id === value)?.name ?? 'Stored in Ship'}
                      onChange={(event) => setCrewData(stargraveCrewService.assignTechnology(selectedWarbandId, instance.instanceId, event.target.value || null))}>
                      <MenuItem value="">Stored in Ship</MenuItem>
                      {technologyOwners.map((owner) => <MenuItem key={owner.id} value={owner.id}>{owner.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                  {!mini && <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
                    {(instance.remainingUses !== undefined || technology.useBuffId || technology.healAmount !== undefined) && (
                      <Button onClick={() => setUseTechnologyTargetId(instance.instanceId)}>Use</Button>
                    )}
                    <Button color="error" onClick={() => setInventoryAction({ kind: 'technology', instanceId: instance.instanceId, itemName: technology.name, action: 'discard', salePrice: technology.salePrice })}>Discard</Button>
                    <Button onClick={() => setInventoryAction({ kind: 'technology', instanceId: instance.instanceId, itemName: technology.name, action: 'sell', salePrice: technology.salePrice })}>Sell</Button>
                  </Stack>}
                </Paper>;
                return <React.Fragment key={instance.instanceId}>{technologyCard(isMinified)}</React.Fragment>;
              })}
            </Box>
          )}
          </>}
          {advancedInventoryTab === 1 && <>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
            <Typography variant="h5">Advanced Weapons</Typography>
          </Stack>
          {crewData.advancedWeapons.length === 0 ? <Typography color="text.secondary">No Advanced Weapons stored.</Typography> : (
            <Box sx={isMinified ? miniFlexSx : { display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
              {crewData.advancedWeapons.map((instance) => {
                const weapon = stargraveAdvancedWeapons.find((candidate) => candidate.id === instance.weaponId);
                if (!weapon) return null;
                const eligibleOwners = technologyOwners.filter((owner) => {
                  const leader = [crewData.captain, crewData.firstMate].find((candidate) => candidate?.id === owner.id);
                  const soldier = crewData.soldiers.find((candidate) => candidate.id === owner.id);
                  const equipmentIds = leader?.gearIds ?? stargraveSoldiers.find((profile) => profile.id === soldier?.profileId)?.equipmentIds ?? [];
                  return equipmentIds.includes(weapon.baseWeaponId);
                });
                const weaponCard = (mini: boolean): React.ReactElement => <Paper variant="outlined" sx={{ p: 2, ...(mini ? { width: 280, minHeight: 140, boxSizing: 'border-box' } : {}) }}>
                  <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                    <Typography variant="h6" noWrap={mini} sx={{ flex: 1, minWidth: 0 }}>{weapon.name}</Typography>
                    {mini && <MiniCardExpand label={weapon.name} fullCard={weaponCard(false)} />}
                  </Stack>
                  {!mini && <Typography variant="body2" sx={{ my: 1 }}>{weapon.summary}</Typography>}
                  <Stack direction="row" spacing={1} sx={{ my: 1 }}>
                    <Chip size="small" label="Indestructible" />
                    <Chip size="small" label={`Sell ${weapon.salePrice}cr`} />
                  </Stack>
                  <FormControl fullWidth size="small">
                    <InputLabel id={`weapon-owner-${instance.instanceId}`}>Owner</InputLabel>
                    <Select labelId={`weapon-owner-${instance.instanceId}`} label="Owner" value={instance.ownerId ?? ''} displayEmpty
                      renderValue={(value) => technologyOwners.find((owner) => owner.id === value)?.name ?? 'Stored in Ship'}
                      onChange={(event) => setCrewData(stargraveCrewService.assignAdvancedWeapon(selectedWarbandId, instance.instanceId, event.target.value || null))}>
                      <MenuItem value="">Stored in Ship</MenuItem>
                      {eligibleOwners.map((owner) => <MenuItem key={owner.id} value={owner.id}>{owner.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                  {!mini && <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
                    <Button color="error" onClick={() => setInventoryAction({ kind: 'weapon', instanceId: instance.instanceId, itemName: weapon.name, action: 'discard', salePrice: weapon.salePrice })}>Discard</Button>
                    <Button onClick={() => setInventoryAction({ kind: 'weapon', instanceId: instance.instanceId, itemName: weapon.name, action: 'sell', salePrice: weapon.salePrice })}>Sell</Button>
                  </Stack>}
                </Paper>;
                return <React.Fragment key={instance.instanceId}>{weaponCard(isMinified)}</React.Fragment>;
              })}
            </Box>
          )}
          </>}
          {advancedInventoryTab === 2 && <>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
            <Typography variant="h5">Alien Artefacts</Typography>
          </Stack>
          {crewData.alienArtefacts.length === 0 ? <Typography color="text.secondary">No Alien Artefacts stored.</Typography> : (
            <Box sx={isMinified ? miniFlexSx : { display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
              {crewData.alienArtefacts.map((instance) => {
                const artefact = stargraveAlienArtefacts.find((candidate) => candidate.id === instance.artefactId);
                if (!artefact) return null;
                const eligibleOwners = [crewData.captain, crewData.firstMate]
                  .filter((leader): leader is StargraveLeader => Boolean(leader))
                  .filter((leader) => !crewData.alienArtefacts.some((candidate) =>
                    candidate.instanceId !== instance.instanceId && candidate.ownerId === leader.id && candidate.artefactId === instance.artefactId
                  ));
                const artefactCard = (mini: boolean): React.ReactElement => <Paper variant="outlined" sx={{ p: 2, ...(mini ? { width: 280, minHeight: 140, boxSizing: 'border-box' } : {}) }}>
                  <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                    <Typography variant="h6" noWrap={mini} sx={{ flex: 1, minWidth: 0 }}>{artefact.name}</Typography>
                    {mini && <MiniCardExpand label={artefact.name} fullCard={artefactCard(false)} />}
                  </Stack>
                  {!mini && <Typography variant="body2" sx={{ my: 1 }}>{artefact.summary}</Typography>}
                  <Stack direction="row" spacing={1} sx={{ my: 1 }}>
                    <Chip size="small" label="1 slot" />
                    {artefact.uses !== undefined && <Chip size="small" label={`${instance.remainingUses ?? artefact.uses}/${artefact.uses} uses`} />}
                    <Chip size="small" label={`Sell ${artefact.salePrice}cr`} />
                  </Stack>
                  <FormControl fullWidth size="small">
                    <InputLabel id={`artefact-owner-${instance.instanceId}`}>Owner</InputLabel>
                    <Select labelId={`artefact-owner-${instance.instanceId}`} label="Owner" value={instance.ownerId ?? ''} displayEmpty
                      renderValue={(value) => technologyOwners.find((owner) => owner.id === value)?.name ?? 'Stored in Ship'}
                      onChange={(event) => setCrewData(stargraveCrewService.assignAlienArtefact(selectedWarbandId, instance.instanceId, event.target.value || null))}>
                      <MenuItem value="">Stored in Ship</MenuItem>
                      {eligibleOwners.map((owner) => <MenuItem key={owner.id} value={owner.id}>{owner.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                  {!mini && <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
                    {artefact.uses !== undefined && (
                      <Button
                        disabled={(instance.remainingUses ?? artefact.uses) <= 0}
                        onClick={() => setCrewData(stargraveCrewService.useAlienArtefact(selectedWarbandId, instance.instanceId))}
                      >Use</Button>
                    )}
                    <Button color="error" onClick={() => setInventoryAction({ kind: 'artefact', instanceId: instance.instanceId, itemName: artefact.name, action: 'discard', salePrice: artefact.salePrice })}>Discard</Button>
                    <Button onClick={() => setInventoryAction({ kind: 'artefact', instanceId: instance.instanceId, itemName: artefact.name, action: 'sell', salePrice: artefact.salePrice })}>Sell</Button>
                  </Stack>}
                </Paper>;
                return <React.Fragment key={instance.instanceId}>{artefactCard(isMinified)}</React.Fragment>;
              })}
            </Box>
          )}
          </>}
        </Box>

        <Box key="ship" sx={steelPanelSx}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={2}>
            {editingShipName ? (
              <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                <TextField size="small" label="Ship Name" value={shipName} onChange={(event) => setShipName(event.target.value)} />
                <Button onClick={() => {
                  setCrewData(stargraveCrewService.renameShip(selectedWarbandId, shipName));
                  setEditingShipName(false);
                }} disabled={!shipName.trim()}>Save</Button>
              </Stack>
            ) : (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h5">{crewData.ship.name}</Typography>
                <Button size="small" onClick={() => { setShipName(crewData.ship.name); setEditingShipName(true); }}>Rename</Button>
              </Stack>
            )}
          </Stack>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={2}>
              <Box>
                <Typography variant="h6">Secrets ({crewData.secrets})</Typography>
                <Typography variant="body2" color="text.secondary">Stored aboard the ship until redeemed or sold.</Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button disabled={crewData.secrets === 0} onClick={() => setSecretAction('discard')}>Discard</Button>
                <Button disabled={crewData.secrets === 0} onClick={() => setSecretAction('sell')}>Sell One</Button>
                <Button variant="contained" disabled={crewData.secrets === 0} onClick={() => setSecretModalOpen(true)}>Redeem</Button>
              </Stack>
            </Stack>
          </Paper>
          <Typography variant="subtitle1">Upgrades ({crewData.ship.upgradeIds.length})</Typography>
          {crewData.ship.upgradeIds.length === 0 ? (
            <Typography color="text.secondary">No ship upgrades installed.</Typography>
          ) : (
            <Box sx={isMinified ? miniFlexSx : { display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
              {crewData.ship.upgradeIds.map((id, index) => {
                const upgrade = stargraveShipUpgrades.find((candidate) => candidate.id === id);
                if (!upgrade) return null;
                const upgradeCard = (mini: boolean): React.ReactElement => <Paper variant="outlined" sx={{ p: 2, ...(mini ? { width: 280, minHeight: 72, boxSizing: 'border-box' } : {}) }}>
                  <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                    <Typography variant="h6" noWrap={mini} sx={{ flex: 1, minWidth: 0 }}>{upgrade.name}</Typography>
                    {mini && <MiniCardExpand label={upgrade.name} fullCard={upgradeCard(false)} />}
                  </Stack>
                  {!mini && <Typography variant="body2" sx={{ mt: 1 }}>{upgrade.summary}</Typography>}
                  {!mini && <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                    <Button color="error" onClick={() => setInventoryAction({ kind: 'shipUpgrade', instanceId: String(index), itemName: upgrade.name, action: 'discard', salePrice: upgrade.cost })}>Discard</Button>
                  </Stack>}
                </Paper>;
                return <React.Fragment key={`${id}-${index}`}>{upgradeCard(isMinified)}</React.Fragment>;
              })}
            </Box>
          )}
        </Box>

        <Box key="index" sx={steelPanelSx}>
          <StargraveIndexTab />
        </Box>
      </SwipeTabs>}

      {isPlayMode && <SwipeTabs containerRef={playContainerRef} trackRef={playTrackRef} numTabs={3} panelPb={10}>
        <Box sx={steelPanelSx}>
          <StargravePlayTab
            warbandId={selectedWarbandId}
            crewData={crewData}
            minified={isMinified}
            onDamage={(memberId, amount) => setCrewData(stargraveCrewService.applyDamage(selectedWarbandId, memberId, amount))}
            onHeal={(memberId, amount) => setCrewData(stargraveCrewService.applyHealing(selectedWarbandId, memberId, amount))}
            onStateChange={(memberId, changes) => setCrewData(stargraveCrewService.updateMemberGameState(selectedWarbandId, memberId, changes))}
            onAddBuff={(memberId, buffId) => setCrewData(stargraveCrewService.addMemberBuff(selectedWarbandId, memberId, buffId))}
            onRemoveBuff={(memberId, buffId) => setCrewData(stargraveCrewService.removeMemberBuff(selectedWarbandId, memberId, buffId))}
            onUseTechnology={(instanceId, options) => setCrewData(stargraveCrewService.useTechnology(selectedWarbandId, instanceId, options))}
            onUseArtefact={(instanceId) => setCrewData(stargraveCrewService.useAlienArtefact(selectedWarbandId, instanceId))}
            onEndGame={() => navigateToPage('postgame')}
          />
        </Box>
        <Box sx={steelPanelSx}>
          <StargravePlayPowersTab crewData={crewData} minified={isMinified} />
        </Box>
        <Box sx={steelPanelSx}>
          <StargraveIndexTab />
        </Box>
      </SwipeTabs>}

      {isPreGameMode && (
        <Box sx={steelPanelSx}>
          <StargravePreGameTab
            crewData={crewData}
            availableCredits={crew.gold}
            onAddTemporarySoldier={() => setRecruitTemporaryOpen(true)}
            onRemoveSoldier={(soldierId) => setCrewData(stargraveCrewService.removeMember(selectedWarbandId, soldierId))}
            onStartGame={(options) => {
            const result = stargraveCrewService.startGame(selectedWarbandId, options);
            setCrewData(result.stargrave);
            setWarbands(useWarbandStore.getState().warbands.map((warband) =>
              warband.id === selectedWarbandId ? result.warband : warband
            ));
            navigateToPage('play');
          }} />
        </Box>
      )}

      {isPostGameMode && (
        <Box sx={steelPanelSx}>
          <StargravePostGameTab crewData={crewData} onComplete={(resolutions, experience, credits, lootResolutions, freeRobots) => {
            const result = stargraveCrewService.completePostGame(selectedWarbandId, resolutions, experience, credits, lootResolutions, freeRobots);
            applyTechnologyResult(result);
            navigateToPage('pregame');
            navigateToPage('manage');
          }} />
        </Box>
      )}

      {isManageMode && <Paper sx={{ ...tabsSurface, position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1100 }} elevation={3}>
        <BottomNavigation
          showLabels
          value={manageTabIndex}
          onChange={(_, value) => setManageTabIndex(value)}
          aria-label="Stargrave crew sections"
        >
          <BottomNavigationAction label="Crew" icon={<AccessibilityIcon />} />
          <BottomNavigationAction label="Powers" icon={<AutoFixHighIcon />} />
          <BottomNavigationAction label="Cargo" icon={<Inventory2Icon />} />
          <BottomNavigationAction label="Ship" icon={<RocketLaunchIcon />} />
          <BottomNavigationAction label="Index" icon={<MenuBookIcon />} />
        </BottomNavigation>
      </Paper>}
      {isPlayMode && <Paper sx={{ ...tabsSurface, position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1100 }} elevation={3}>
        <BottomNavigation
          showLabels
          value={playTabIndex}
          onChange={(_, value) => setPlayTabIndex(value)}
          aria-label="Stargrave play sections"
        >
          <BottomNavigationAction label="Play" icon={<AccessibilityIcon />} />
          <BottomNavigationAction label="Powers" icon={<AutoFixHighIcon />} />
          <BottomNavigationAction label="Index" icon={<MenuBookIcon />} />
        </BottomNavigation>
      </Paper>}
      {leaderRole && <StargraveLeaderModal
        open
        role={leaderRole}
        onClose={() => setLeaderRole(null)}
        onSave={saveLeader}
      />}
      <StargraveRecruitSoldierModal
        open={recruitOpen || Boolean(editingSoldier)}
        availableCredits={crew.gold + (editingSoldier ? stargraveSoldiers.find((profile) => profile.id === editingSoldier.profileId)?.cost ?? 0 : 0)}
        initialSoldier={editingSoldier ?? undefined}
        onClose={() => { setRecruitOpen(false); setEditingSoldier(null); }}
        onRecruit={recruitSoldier}
        onEdit={editSoldier}
      />
      <StargraveRecruitSoldierModal
        open={recruitTemporaryOpen}
        availableCredits={Number.MAX_SAFE_INTEGER}
        onClose={() => setRecruitTemporaryOpen(false)}
        onRecruit={(input) => setCrewData(stargraveCrewService.recruitTemporarySoldier(selectedWarbandId, input))}
      />
      <ItemDetailsPopover
        popover={powerPopover}
        onClose={() => { setPowerPopover(null); setTechnologyPopoverInstanceId(null); }}
        showUseAction={Boolean(technologyPopoverInstance && (() => {
          const technology = stargraveAdvancedTechnology.find((candidate) => candidate.id === technologyPopoverInstance.technologyId);
          return technology && (technology.useBuffId || technology.healAmount !== undefined || technologyPopoverInstance.remainingUses !== undefined);
        })())}
        useDisabled={technologyPopoverInstance?.remainingUses !== undefined && technologyPopoverInstance.remainingUses <= 0}
        remainingUsesLabel={technologyPopoverInstance?.remainingUses !== undefined
          ? `${technologyPopoverInstance.remainingUses} uses remaining`
          : undefined}
        onUse={() => {
          if (!technologyPopoverInstanceId) return;
          setUseTechnologyTargetId(technologyPopoverInstanceId);
          setPowerPopover(null);
          setTechnologyPopoverInstanceId(null);
        }}
      />
      {(() => {
        const instance = useTechnologyTargetId ? crewData.advancedTechnology.find((item) => item.instanceId === useTechnologyTargetId) : undefined;
        const technology = instance ? stargraveAdvancedTechnology.find((candidate) => candidate.id === instance.technologyId) : undefined;
        return instance && technology ? <UseTechnologyDialog
          instance={instance}
          technology={technology}
          members={technologyOwners}
          onClose={() => setUseTechnologyTargetId(null)}
          onConfirm={(options) => {
            setCrewData(stargraveCrewService.useTechnology(selectedWarbandId, instance.instanceId, options));
            setUseTechnologyTargetId(null);
          }}
        /> : null;
      })()}
      <StargraveTechnologyModal
        open={technologyModalOpen}
        availableCredits={crew.gold}
        onClose={() => setTechnologyModalOpen(false)}
        onAdd={(technologyId, addForFree) => applyTechnologyResult(stargraveCrewService.addTechnology(selectedWarbandId, technologyId, addForFree))}
      />
      <StargraveShipUpgradeModal
        open={shipUpgradeModalOpen}
        availableCredits={crew.gold}
        installedUpgradeIds={crewData.ship.upgradeIds}
        onClose={() => setShipUpgradeModalOpen(false)}
        onPurchase={(upgradeId, addForFree) => applyTechnologyResult(stargraveCrewService.purchaseShipUpgrade(selectedWarbandId, upgradeId, addForFree))}
      />
      {(() => {
        const buffMember = findCrewMember(manageBuffsMemberId);
        return buffMember ? <StargraveManageBuffsModal
          open
          memberName={buffMember.name}
          activeBuffs={buffMember.gameState.activeBuffs}
          onClose={() => setManageBuffsMemberId(null)}
          onAdd={(buffId) => setCrewData(stargraveCrewService.addMemberBuff(selectedWarbandId, buffMember.id, buffId))}
        /> : null;
      })()}
      <ConfirmActionDialog
        open={buffDelete !== null}
        title="Remove buff / debuff"
        message={buffDelete ? `Remove "${getStargraveBuffById(buffDelete.buffId)?.name}" from ${findCrewMember(buffDelete.memberId)?.name}?` : ''}
        confirmLabel="Remove"
        confirmColor="error"
        onClose={() => setBuffDelete(null)}
        onConfirm={() => {
          if (buffDelete) setCrewData(stargraveCrewService.removeMemberBuff(selectedWarbandId, buffDelete.memberId, buffDelete.buffId));
          setBuffDelete(null);
        }}
      />
      {renameTarget && <EditNameDialog
        open
        label="Name"
        initialValue={renameTarget.name}
        onClose={() => setRenameTarget(null)}
        onSave={saveRename}
      />}
      <ManageGoldModal
        open={editCrewStat !== null}
        warband={crew}
        onClose={() => setEditCrewStat(null)}
        title={editCrewStat === 'experience' ? 'Manage Experience' : 'Manage Credits'}
        label={editCrewStat === 'experience' ? 'Experience (XP)' : 'Credits (cr)'}
        field={editCrewStat ?? 'gold'}
      />
      <Menu anchorEl={rosterMenuAnchor} open={Boolean(rosterMenuAnchor)} onClose={closeRosterMenu}>
        <MenuItem onClick={() => {
          if (rosterTarget) {
            const leader = [crewData.captain, crewData.firstMate].find((candidate) => candidate?.id === rosterTarget.id);
            if (leader) setEditingLeader(leader);
            else {
              const soldier = crewData.soldiers.find((candidate) => candidate.id === rosterTarget.id);
              if (soldier) setEditingSoldier(soldier);
            }
          }
          closeRosterMenu();
        }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit Details
        </MenuItem>
        {(() => {
          const rosterSoldier = rosterTarget ? crewData.soldiers.find((soldier) => soldier.id === rosterTarget.id) : undefined;
          return rosterSoldier ? <MenuItem onClick={() => {
            const result = stargraveCrewService.updateSoldier(selectedWarbandId, rosterSoldier.id, {
              profileId: rosterSoldier.profileId,
              name: rosterSoldier.name,
              notes: rosterSoldier.notes,
              isRobot: !rosterSoldier.isRobot,
            });
            setCrewData(result.stargrave);
            setWarbands(useWarbandStore.getState().warbands.map((warband) =>
              warband.id === selectedWarbandId ? result.warband : warband
            ));
            closeRosterMenu();
          }}>
            <ListItemIcon>{rosterSoldier.isRobot ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
            <ListItemText>Robot</ListItemText>
          </MenuItem> : null;
        })()}
        {(() => {
          const rosterSoldier = rosterTarget ? crewData.soldiers.find((soldier) => soldier.id === rosterTarget.id) : undefined;
          return rosterSoldier ? <MenuItem onClick={() => {
            setCrewData(stargraveCrewService.setSoldierStoredInShip(selectedWarbandId, rosterSoldier.id, !rosterSoldier.storedInShip));
            closeRosterMenu();
          }}>
            <ListItemIcon>{rosterSoldier.storedInShip ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
            <ListItemText>Store In Ship</ListItemText>
          </MenuItem> : null;
        })()}
        <MenuItem onClick={() => {
          if (rosterTarget) setRemoveTarget(rosterTarget);
          closeRosterMenu();
        }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Remove
        </MenuItem>
      </Menu>
      {editingLeader && <StargraveLeaderModal
        open
        role={editingLeader.role}
        mode="edit"
        initialLeader={editingLeader}
        initialStep={editingLeaderPowersOnly ? 2 : 0}
        powersOnly={editingLeaderPowersOnly}
        onClose={() => { setEditingLeader(null); setEditingLeaderPowersOnly(false); }}
        onSave={saveEditedLeader}
      />}
      <ConfirmActionDialog
        open={Boolean(removeTarget)}
        title={`Remove ${removeTarget?.roleLabel ?? 'Crew Member'}`}
        message={`Remove ${removeTarget?.name ?? 'this crew member'}? Assigned Advanced Technology will be returned to ship storage. No credits will be refunded.`}
        confirmLabel="Remove"
        confirmColor="error"
        onClose={() => setRemoveTarget(null)}
        onConfirm={() => {
          if (!removeTarget) return;
          setCrewData(stargraveCrewService.removeMember(selectedWarbandId, removeTarget.id));
          setRemoveTarget(null);
        }}
      />
      <StargraveAdvanceLeaderModal
        open={advanceModalOpen}
        crewData={crewData}
        experience={crew.experience}
        onClose={() => setAdvanceModalOpen(false)}
        onAdvance={(role, choice) => applyTechnologyResult(stargraveCrewService.advanceLeader(selectedWarbandId, role, choice))}
      />
      {injuryLeader && <SelectPermanentInjuryModal
        open
        memberName={injuryLeader.name}
        injuries={stargravePermanentInjuries
          .filter((injury) => canAddPermanentInjury(injuryLeader.permanentInjuryIds, injury))
          .map((injury) => ({
            id: injury.id,
            name: injury.name,
            description: injury.summary,
            effectLabel: (Object.entries(injury.statModifiers ?? {}) as [string, number][])
              .map(([stat, value]) => `${stat.charAt(0).toUpperCase()}${stat.slice(1)} ${value > 0 ? '+' : ''}${value}`)
              .join(', ') || undefined,
          }))}
        onClose={() => setInjuryLeader(null)}
        onSelect={(injury) => {
          setCrewData(stargraveCrewService.addLeaderPermanentInjury(selectedWarbandId, injuryLeader.id, injury.id));
          setInjuryLeader(null);
        }}
      />}
      <StargraveAdvancedWeaponModal
        open={advancedWeaponModalOpen}
        availableCredits={crew.gold}
        onClose={() => setAdvancedWeaponModalOpen(false)}
        onAdd={(weaponId, addForFree) => applyTechnologyResult(stargraveCrewService.addAdvancedWeapon(selectedWarbandId, weaponId, addForFree))}
      />
      <StargraveAlienArtefactModal
        open={alienArtefactModalOpen}
        availableCredits={crew.gold}
        onClose={() => setAlienArtefactModalOpen(false)}
        onAdd={(artefactId, addForFree) => applyTechnologyResult(stargraveCrewService.addAlienArtefact(selectedWarbandId, artefactId, addForFree))}
      />
      <StargraveSecretModal
        open={secretModalOpen}
        onClose={() => setSecretModalOpen(false)}
        onRedeem={(reward) => applyTechnologyResult(stargraveCrewService.spendSecret(selectedWarbandId, 'redeem', reward))}
      />
      <ConfirmActionDialog
        open={Boolean(secretAction)}
        title={secretAction === 'sell' ? 'Sell Secret' : 'Discard Secret'}
        message={secretAction === 'sell'
          ? 'Sell one stored Secret for 200cr?'
          : 'Discard one Secret after a failed opportunity? This cannot be undone.'}
        confirmLabel={secretAction === 'sell' ? 'Sell' : 'Discard'}
        confirmColor={secretAction === 'discard' ? 'error' : 'primary'}
        onClose={() => { setSecretAction(null); setRefundOnDiscard(false); }}
        onConfirm={() => {
          if (!secretAction) return;
          applyTechnologyResult(stargraveCrewService.spendSecret(
            selectedWarbandId,
            secretAction === 'discard' && refundOnDiscard ? 'sell' : secretAction
          ));
          setSecretAction(null);
          setRefundOnDiscard(false);
        }}
      >
        {secretAction === 'discard' && <FormControlLabel
          control={<Checkbox checked={refundOnDiscard} onChange={(event) => setRefundOnDiscard(event.target.checked)} />}
          label="Refund sale price (200cr)"
        />}
      </ConfirmActionDialog>
      <ConfirmActionDialog
        open={Boolean(inventoryAction)}
        title={`${inventoryAction?.action === 'sell' ? 'Sell' : 'Discard'} ${inventoryAction?.itemName ?? 'Item'}`}
        message={inventoryAction?.action === 'sell'
          ? `Sell ${inventoryAction.itemName} for ${inventoryAction.salePrice}cr?`
          : `Discard ${inventoryAction?.itemName ?? 'this item'}? This cannot be undone.`}
        confirmLabel={inventoryAction?.action === 'sell' ? 'Sell' : 'Discard'}
        confirmColor={inventoryAction?.action === 'discard' ? 'error' : 'primary'}
        onClose={() => { setInventoryAction(null); setRefundOnDiscard(false); }}
        onConfirm={() => {
          if (!inventoryAction) return;
          const refund = inventoryAction.action === 'sell' || refundOnDiscard;
          const result = inventoryAction.kind === 'technology'
            ? stargraveCrewService.removeTechnology(selectedWarbandId, inventoryAction.instanceId, refund)
            : inventoryAction.kind === 'weapon'
              ? stargraveCrewService.removeAdvancedWeapon(selectedWarbandId, inventoryAction.instanceId, refund)
              : inventoryAction.kind === 'artefact'
                ? stargraveCrewService.removeAlienArtefact(selectedWarbandId, inventoryAction.instanceId, refund)
                : stargraveCrewService.removeShipUpgrade(selectedWarbandId, Number(inventoryAction.instanceId), refund);
          applyTechnologyResult(result);
          setInventoryAction(null);
          setRefundOnDiscard(false);
        }}
      >
        {inventoryAction?.action === 'discard' && <FormControlLabel
          control={<Checkbox checked={refundOnDiscard} onChange={(event) => setRefundOnDiscard(event.target.checked)} />}
          label={inventoryAction.kind === 'shipUpgrade'
            ? `Refund cost (${inventoryAction.salePrice}cr)`
            : `Refund sale price (${inventoryAction.salePrice}cr)`}
        />}
      </ConfirmActionDialog>
      {isManageMode && manageTabIndex === 0 && (
        <Fab
          variant="extended"
          color="primary"
          size="medium"
          onClick={() => setRecruitOpen(true)}
          sx={{ position: 'fixed', bottom: 72, right: 16, zIndex: 1200 }}
        >
          <AddIcon sx={{ mr: 1 }} />
          Recruit Soldier
        </Fab>
      )}
      {isManageMode && manageTabIndex === 2 && (
        <Fab
          variant="extended"
          color="primary"
          size="medium"
          onClick={() => {
            if (advancedInventoryTab === 0) setTechnologyModalOpen(true);
            else if (advancedInventoryTab === 1) setAdvancedWeaponModalOpen(true);
            else setAlienArtefactModalOpen(true);
          }}
          sx={{ position: 'fixed', bottom: 72, right: 16, zIndex: 1200 }}
        >
          <AddIcon sx={{ mr: 1 }} />
          {advancedInventoryTab === 0 ? 'Add Technology' : advancedInventoryTab === 1 ? 'Add Weapon' : 'Add Artefact'}
        </Fab>
      )}
      {isManageMode && manageTabIndex === 3 && (
        <Fab
          variant="extended"
          color="primary"
          size="medium"
          onClick={() => setShipUpgradeModalOpen(true)}
          sx={{ position: 'fixed', bottom: 72, right: 16, zIndex: 1200 }}
        >
          <AddIcon sx={{ mr: 1 }} />
          Install Upgrade
        </Fab>
      )}
      <Fab
        color="primary"
        size="small"
        aria-label={isMinified ? 'Zoom in' : 'Zoom out'}
        onClick={() => setIsMinified(!isMinified)}
        sx={{ position: 'fixed', top: 85, right: 16, zIndex: 50 }}
      >
        {isMinified ? <ZoomInIcon /> : <ZoomOutIcon />}
      </Fab>
      <Fab
        color="primary"
        aria-label={isManageMode
          ? lastNonManagePage === 'play' ? 'Enter play mode' : 'Enter pre-game setup'
          : 'Return to manage mode'}
        onClick={() => navigateToPage(isManageMode ? lastNonManagePage : 'manage')}
        sx={{
          position: 'fixed',
          bottom: 56,
          left: 16,
          zIndex: 1200,
          backgroundColor: isManageMode ? alpha(lightGreen.A400, 0.52) : alpha(orange.A400, 0.52),
          '&:hover': {
            backgroundColor: isManageMode ? alpha(lightGreen.A400, 0.65) : alpha(orange.A400, 0.65),
          },
        }}
      >
        {isManageMode ? <PlayArrowIcon /> : <SettingsIcon />}
      </Fab>
    </Box>
  );
};